import { CANVAS_BASE_WIDTH, CANVAS_BASE_HEIGHT } from './constants';
import linkIconSvg from '../images/linkIcon.svg';

// Pré-charger l'icône de lien
const linkIconImage = new Image();
linkIconImage.src = linkIconSvg;


/**
 * Calcule les coordonnées snappées à la grille centrée
 * La grille est centrée, donc le snap se fait par rapport au centre
 * @param {number} pixelX - Coordonnée X en pixels sur le canvas
 * @param {number} pixelY - Coordonnée Y en pixels sur le canvas
 * @param {number} canvasPixelWidth - Largeur du canvas en pixels
 * @param {number} canvasPixelHeight - Hauteur du canvas en pixels
 * @param {number} gridColumns - Nombre de colonnes de la grille
 * @param {number} gridRows - Nombre de lignes de la grille
 * @returns {{ snapX: number, snapY: number }} - Coordonnées snappées en pixels
 */
export function snapToGrid(pixelX, pixelY, canvasPixelWidth, canvasPixelHeight, gridColumns, gridRows) {
  const centerX = canvasPixelWidth / 2;
  const centerY = canvasPixelHeight / 2;
  const cellSizeX = canvasPixelWidth / gridColumns;
  const cellSizeY = canvasPixelHeight / gridRows;
  
  // Calculer la distance au centre en nombre de cellules, arrondir, puis recalculer
  const cellsFromCenterX = Math.round((pixelX - centerX) / cellSizeX);
  const cellsFromCenterY = Math.round((pixelY - centerY) / cellSizeY);
  
  // Recalculer la position snappée
  let snapX = centerX + cellsFromCenterX * cellSizeX;
  let snapY = centerY + cellsFromCenterY * cellSizeY;
  
  // Limiter aux bornes du canvas (ne pas sortir de la scène)
  // On laisse une marge d'une demi-cellule pour éviter d'être pile sur le bord
  const minX = 0;
  const maxX = canvasPixelWidth;
  const minY = 0;
  const maxY = canvasPixelHeight;
  
  snapX = Math.max(minX, Math.min(maxX, snapX));
  snapY = Math.max(minY, Math.min(maxY, snapY));
  
  return { snapX, snapY };
}


/**
 * Dessine la scène complète (grille + positions de la période courante)
 */
export function drawScene({
  canvasRef,
  currentSession,
  currentPeriodIndex,
  canvasPixelWidth,
  canvasPixelHeight,
  periods,
  selectedPositionId,
  dancers,
  linkChain = [],
  gridColumns = 28,
  gridRows = 18,
}) {
  if (!canvasRef.current || !currentSession) return;
  const ctx = canvasRef.current.getContext('2d');
  ctx.clearRect(0, 0, canvasPixelWidth, canvasPixelHeight);

  drawGrid(ctx, canvasPixelWidth, canvasPixelHeight, periods, gridColumns, gridRows);

  const currentPeriod = currentSession.periods?.[currentPeriodIndex];
  if (!currentPeriod) return;

  currentPeriod.positions?.forEach((pos) => {
    const isInLinkChain = linkChain.some(p => p.id === pos.id);
    drawPosition(ctx, pos, canvasPixelWidth, canvasPixelHeight, selectedPositionId, dancers, isInLinkChain);
  });
}

/**
 * Dessine la grille sur le canvas
 * @param {number} gridColumns - Nombre de colonnes (défaut: 28)
 * @param {number} gridRows - Nombre de lignes (défaut: 18)
 */
export function drawGrid(ctx, canvasPixelWidth, canvasPixelHeight, periods, gridColumns = 28, gridRows = 18) {
  if (!periods || periods.length === 0) {
    ctx.clearRect(0, 0, canvasPixelWidth, canvasPixelHeight);
    return;
  }

  ctx.lineWidth = 1;

  // Lignes centrales
  const centerX = canvasPixelWidth / 2;
  const centerY = canvasPixelHeight / 2;

  ctx.beginPath();
  ctx.moveTo(centerX, 0);
  ctx.lineTo(centerX, canvasPixelHeight);
  ctx.moveTo(0, centerY);
  ctx.lineTo(canvasPixelWidth, centerY);
  ctx.strokeStyle = 'black';
  ctx.stroke();

  // Lignes en pointillés
  ctx.beginPath();
  const cellSizeX = canvasPixelWidth / gridColumns;
  const cellSizeY = canvasPixelHeight / gridRows;

  // vertical
  for (let i = 1; i < gridColumns / 2; i++) {
    ctx.moveTo(centerX + i * cellSizeX, 0);
    ctx.lineTo(centerX + i * cellSizeX, canvasPixelHeight);
    ctx.moveTo(centerX - i * cellSizeX, 0);
    ctx.lineTo(centerX - i * cellSizeX, canvasPixelHeight);
  }
  // horizontal
  for (let i = 1; i < gridRows / 2; i++) {
    ctx.moveTo(0, centerY + i * cellSizeY);
    ctx.lineTo(canvasPixelWidth, centerY + i * cellSizeY);
    ctx.moveTo(0, centerY - i * cellSizeY);
    ctx.lineTo(canvasPixelWidth, centerY - i * cellSizeY);
  }
  ctx.strokeStyle = 'gray';
  ctx.setLineDash([2, 2]);
  ctx.stroke();
  ctx.setLineDash([]);
}

/**
 * Dessine une position en tenant compte du scaling
 * @param {boolean} isInLinkChain - Si true, affiche la position en vert (sélectionnée pour liaison)
 * @param {boolean} hideLinks - Si true, ne pas afficher le badge de lien (pour les interpolations)
 */
export function drawPosition(ctx, pos, canvasPixelWidth, canvasPixelHeight, selectedPositionId, dancers, isInLinkChain = false, hideLinks = false) {
  const scaledX = (pos.x / CANVAS_BASE_WIDTH) * canvasPixelWidth;
  const scaledY = (pos.y / CANVAS_BASE_HEIGHT) * canvasPixelHeight;
  const r = 6;
  
  // Si la position est dans la chaîne de liaison en cours, on la colorie en vert
  let color = pos.dancer_id ? 'blue' : 'gray';
  if (isInLinkChain) {
    color = '#00cc66'; // Vert pour les positions dans linkChain
  }

  // 1) Cercle principal
  ctx.beginPath();
  ctx.arc(scaledX, scaledY, r, 0, 2 * Math.PI);
  ctx.fillStyle = color;
  ctx.fill();

  // Si sélectionné
  if (pos.id === selectedPositionId) {
    ctx.save();
    ctx.strokeStyle = 'red';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
  }

  // 2) Badge "link" si la position est liée (a un link_group_id) et qu'on ne les cache pas
  if (pos.link_group_id && !hideLinks) {
    drawLinkBadge(ctx, scaledX, scaledY, r);
  }

  // 3) Nom du dancer
  const dancer = dancers.find((d) => d.id === pos.dancer_id);
  const label = dancer?.name || 'n/a';
  ctx.fillStyle = 'black';
  ctx.font = '13px sans-serif';
  ctx.fillText(label, scaledX + r + 3, scaledY + r + 3);

  // 4) Afficher les skills sous forme d'étoiles (en haut à gauche)
  if (pos.skills && pos.skills.length > 0) {
    const startX = scaledX + 7;
    const startY = scaledY - 7;
    pos.skills.forEach((sk, i) => {
      const offset = i * 12;
      ctx.fillStyle = sk.color || '#000';
      ctx.font = '12px sans-serif';
      ctx.fillText('★', startX + offset, startY);
    });
  }

  // 5) Afficher les stunts sous forme de cercles non remplis (en bas à gauche)
  if (pos.stunts && pos.stunts.length > 0) {
    const startX = scaledX - 13; // Commence à gauche de la position
    const startY = scaledY + 6; // En dessous de la position
    pos.stunts.forEach((st, i) => {
      const offset = i * 12; // Chaque cercle suivant est décalé vers la gauche
      ctx.beginPath();
      ctx.arc(startX - offset, startY, 4, 0, 2 * Math.PI);
      ctx.strokeStyle = st.color || '#000';
      ctx.lineWidth = 2;
      ctx.stroke();
    });
  }
}

/**
 * Dessine un petit badge "link" (icône de chaîne) au-dessus à gauche d'une position liée
 */
function drawLinkBadge(ctx, x, y, radius) {
  const iconSize = 17;
  const badgeX = x - radius - iconSize / 2 - 7;
  const badgeY = y - radius - iconSize / 2 - 5;
  
  ctx.save();
  if (linkIconImage.complete) {
    ctx.drawImage(linkIconImage, badgeX, badgeY, iconSize, iconSize);
  }
  ctx.restore();
}

/**
 * Dessine la vue trajectoire d'un danseur
 */
export function drawTrajectory({
  canvasRef,
  currentSession,
  selectedDancerId,
  setSelectedDancerId,
  dancers,
  periods,
  canvasPixelWidth,
  canvasPixelHeight,
  gridColumns = 28,
  gridRows = 18,
}) {
  if (!selectedDancerId) {
    if (dancers && dancers.length > 0) {
      setSelectedDancerId(dancers[0].id);
    } else {
      return;
    }
  }

  if (!canvasRef.current || !currentSession) return;
  const ctx = canvasRef.current.getContext('2d');
  ctx.clearRect(0, 0, canvasPixelWidth, canvasPixelHeight);

  drawGrid(ctx, canvasPixelWidth, canvasPixelHeight, periods, gridColumns, gridRows);

  let dancerPositions = [];
  periods.forEach((period) => {
    const pos = period.positions?.find((p) => p.dancer_id === selectedDancerId);
    if (pos) {
      dancerPositions.push({ ...pos, periodName: period.name, start_time: period.start_time });
    }
  });

  // Trier les positions par ordre de start_time
  dancerPositions.sort((a, b) => a.order_index - b.order_index);

  if (dancerPositions.length < 2) {
    dancerPositions.forEach((pos) => 
      drawTrajectoryPosition(ctx, pos, canvasPixelWidth, canvasPixelHeight, selectedDancerId, dancers)
    );
    return;
  }

  for (let i = 0; i < dancerPositions.length; i++) {
    const pos = dancerPositions[i];
    drawTrajectoryPosition(ctx, pos, canvasPixelWidth, canvasPixelHeight, selectedDancerId, dancers);

    if (i < dancerPositions.length - 1) {
      const nextPos = dancerPositions[i + 1];
      drawArrow(ctx, pos, nextPos, canvasPixelWidth, canvasPixelHeight);
    }
  }
}

/**
 * Dessine une position dans la vue trajectoire
 */
export function drawTrajectoryPosition(ctx, pos, canvasPixelWidth, canvasPixelHeight, selectedDancerId, dancers) {
  const scaledX = (pos.x / CANVAS_BASE_WIDTH) * canvasPixelWidth;
  const scaledY = (pos.y / CANVAS_BASE_HEIGHT) * canvasPixelHeight;

  const r = 6;
  ctx.beginPath();
  ctx.arc(scaledX, scaledY, r, 0, 2 * Math.PI);
  ctx.fillStyle = 'blue';
  ctx.fill();

  ctx.fillStyle = 'black';
  ctx.font = '13px sans-serif';
  const selectedDancer = dancers.find((d) => d.id === selectedDancerId);
  const dancerName = selectedDancer ? selectedDancer.name : '???';

  ctx.fillText(
    `${dancerName} (Période: ${pos.periodName})`,
    scaledX + r + 3,
    scaledY + r + 3
  );
}

/**
 * Dessine une flèche entre deux positions
 */
export function drawArrow(ctx, pos1, pos2, canvasPixelWidth, canvasPixelHeight) {
  const x1 = (pos1.x / CANVAS_BASE_WIDTH) * canvasPixelWidth;
  const y1 = (pos1.y / CANVAS_BASE_HEIGHT) * canvasPixelHeight;
  const x2 = (pos2.x / CANVAS_BASE_WIDTH) * canvasPixelWidth;
  const y2 = (pos2.y / CANVAS_BASE_HEIGHT) * canvasPixelHeight;

  ctx.strokeStyle = '#333';
  ctx.fillStyle = '#333';
  ctx.lineWidth = 2;

  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  const headlen = 12;
  const angle = Math.atan2(y2 - y1, x2 - x1);

  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(
    x2 - headlen * Math.cos(angle - Math.PI / 6),
    y2 - headlen * Math.sin(angle - Math.PI / 6)
  );
  ctx.lineTo(
    x2 - headlen * Math.cos(angle + Math.PI / 6),
    y2 - headlen * Math.sin(angle + Math.PI / 6)
  );
  ctx.lineTo(x2, y2);
  ctx.fill();
}

/**
 * Affiche l'interpolation des positions entre deux périodes
 */
export function afficherInterpolationPositions({
  canvasRef,
  currentSession,
  periods,
  currentTime,
  canvasPixelWidth,
  canvasPixelHeight,
  selectedPositionId,
  dancers,
  gridColumns = 28,
  gridRows = 18,
}) {
  if (!canvasRef.current || !currentSession || !periods || periods.length === 0) return;

  const ctx = canvasRef.current.getContext('2d');
  ctx.clearRect(0, 0, canvasPixelWidth, canvasPixelHeight);
  drawGrid(ctx, canvasPixelWidth, canvasPixelHeight, periods, gridColumns, gridRows);

  // Filtrer les périodes qui ont des timestamps valides
  const periodsWithTimestamps = periods.filter(p => p.start_time != null && p.end_time != null);
  
  // Si aucune période n'a de timestamps, afficher la première période en transparence
  if (periodsWithTimestamps.length === 0) {
    const firstPeriod = periods[0];
    if (firstPeriod) {
      ctx.save();
      ctx.globalAlpha = 0.5;
      firstPeriod.positions?.forEach(pos => {
        drawPosition(ctx, pos, canvasPixelWidth, canvasPixelHeight, selectedPositionId, dancers, false, true);
      });
      ctx.restore();
    }
    return;
  }

  const firstPeriod = periodsWithTimestamps[0];
  const lastPeriod = periodsWithTimestamps[periodsWithTimestamps.length - 1];

  // Avant la première période
  if (currentTime < firstPeriod.start_time) {
    ctx.save();
    ctx.globalAlpha = 0.5;
    firstPeriod.positions?.forEach(pos => {
      drawPosition(ctx, pos, canvasPixelWidth, canvasPixelHeight, selectedPositionId, dancers, false, true);
    });
    ctx.restore();
    return;
  }

  // Après la dernière période
  if (currentTime > lastPeriod.end_time) {
    ctx.save();
    ctx.globalAlpha = 0.5;
    lastPeriod.positions?.forEach(pos => {
      drawPosition(ctx, pos, canvasPixelWidth, canvasPixelHeight, selectedPositionId, dancers, false, true);
    });
    ctx.restore();
    return;
  }

  // Entre deux périodes (utiliser les périodes avec timestamps)
  let previousPeriod = null;
  let nextPeriod = null;
  for (let i = 0; i < periodsWithTimestamps.length; i++) {
    if (currentTime < periodsWithTimestamps[i].start_time) {
      nextPeriod = periodsWithTimestamps[i];
      previousPeriod = periodsWithTimestamps[i - 1] || firstPeriod;
      break;
    }
  }

  if (!previousPeriod || !nextPeriod) {
    ctx.save();
    ctx.globalAlpha = 0.5;
    firstPeriod.positions?.forEach(pos => 
      drawPosition(ctx, pos, canvasPixelWidth, canvasPixelHeight, selectedPositionId, dancers, false, true)
    );
    ctx.restore();
    return;
  }

  const gapDuration = nextPeriod.start_time - previousPeriod.end_time;
  const t = gapDuration > 0 ? (currentTime - previousPeriod.end_time) / gapDuration : 0;

  previousPeriod.positions?.forEach(prevPos => {
    if (!prevPos.dancer_id) {
      ctx.save();
      ctx.globalAlpha = 0.5;
      drawPosition(ctx, prevPos, canvasPixelWidth, canvasPixelHeight, selectedPositionId, dancers, false, true);
      ctx.restore();
    } else {
      const nextPos = nextPeriod.positions?.find(pos => pos.dancer_id === prevPos.dancer_id);
      if (nextPos) {
        const interpX = prevPos.x + t * (nextPos.x - prevPos.x);
        const interpY = prevPos.y + t * (nextPos.y - prevPos.y);
        const interpPos = { ...prevPos, x: interpX, y: interpY };
        drawPosition(ctx, interpPos, canvasPixelWidth, canvasPixelHeight, selectedPositionId, dancers, false, true);
      } else {
        ctx.save();
        ctx.globalAlpha = 0.5;
        drawPosition(ctx, prevPos, canvasPixelWidth, canvasPixelHeight, selectedPositionId, dancers, false, true);
        ctx.restore();
      }
    }
  });
}
